
import { useState, useEffect, useCallback } from 'react';
import { searchComponents } from '@/utils/services/componentSearchService';
import { Component } from '@/utils/types/componentTypes';

export interface SearchResult {
  id: string;
  name: string;
  organizationName: string;
  componentTypeId: string;
  icon: string | null;
  component: Component;
}

export function useComponentSearch(subscriptionId?: string) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState<SearchResult | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const performSearch = useCallback(async (term: string) => {
    if (term.length < 3) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      const components = await searchComponents(term, subscriptionId);
      
      const searchResults: SearchResult[] = components.map(component => ({
        id: component.id,
        name: component.name,
        organizationName: (component as any).organizations?.name || 'Sem organização',
        componentTypeId: component.id_component_type,
        icon: component.component_types?.icon || null,
        component
      }));

      setResults(searchResults);
      setIsOpen(searchResults.length > 0);
    } catch (error) {
      console.error('Erro ao buscar componentes:', error);
      setResults([]);
      setIsOpen(false);
    } finally {
      setLoading(false);
    }
  }, [subscriptionId]);

  // Debounce da busca
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        performSearch(searchTerm);
      } else {
        setResults([]);
        setIsOpen(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, performSearch]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setResults([]);
    setSelectedComponent(null);
    setIsOpen(false);
  }, []);

  return {
    searchTerm,
    setSearchTerm,
    results,
    loading,
    selectedComponent,
    setSelectedComponent,
    isOpen,
    setIsOpen,
    clearSearch
  };
}
